"use client";
import React, { useContext, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { createPortal } from "react-dom";
import { Dropdown } from "react-bootstrap-v1.6";

import BottomIconHideWrapper from "@stadium/shared/src/components/bottom-icon-hide-wrapper";

import ToolTip from "@stadium/ui/src/components/tooltip/tooltip";
import Portal from "@stadium/ui/src/components/portal";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import DropdownWrapper, {
  DropdownContext,
} from "@stadium/ui/src/components/bootstrap-dropdown/context";
import {
  CustomDropdownToggleProps,
  DropdownComponentProps,
  CustomDropdownItemProps,
  CustomDropdownHeaderProps,
  ResponsiveDrawerDropdownProps,
} from "@stadium/ui/types/bootstrap-dropdown";

import defaultStyles from "./main.module.scss";

import { FULL_SCREEN, WIDE_SCREEN, NARROW_SCREEN } from "@stadium/helpers/style";

const BOTTOM_DRAWER_DROPDOWN = "bottom-drawer";

/**
 * Use DropdownToggle component as label and heading for DropdownComponent
 */

export const DropdownToggle = ({
  className,
  showDropdownIcon = true,
  children,
  styles = defaultStyles,
  showCloseIcon = false,
  activeClassName = "",
  dropDownIconClass = "",
  truncate = false,
  headingStyle = "default",
  styleType = "border",
  parentClassName = "",
  ...rest
}: CustomDropdownToggleProps) => {
  const { isDropdownOpen } = useContext(DropdownContext);

  const headingStylesMapping = {
    default: styles.defaultHeading,
    uppercase: styles.uppercaseHeading,
    "no-style": "",
    icon: styles.iconButtonDropdown,
  };

  const dDToggleClassName = classNames(className, headingStylesMapping[headingStyle], {
    [`${styles.openDropdown} ${activeClassName}`]: isDropdownOpen,
    [styles.truncateText]: truncate,
  });

  return (
    <Dropdown.Toggle
      {...(Dropdown.Toggle.defaultProps || {})}
      className={`${dDToggleClassName} ${styleType == "no-border" ? styles.noBorder : ""}`}
      bsPrefix={styles.dropdownHeader}
      {...rest}
    >
      {children}
      {showDropdownIcon && <span className={`${styles.dropdownIcon} ${dropDownIconClass}`} />}
      {showCloseIcon && <span className={styles.closeIcon} />}
    </Dropdown.Toggle>
  );
};

const DropdownPortal = ({ children, enabled, parentClassName = "" }) =>
  enabled ? (
    <Portal id="modal-root" dataContainerId="dropdown-portal" className={parentClassName}>
      {children}
    </Portal>
  ) : (
    children
  );

const BootstrapDropdown = ({
  label = null,
  children = null,
  styles = defaultStyles,
  disabled = false,
  menuClassName = "",
  width = "full",
  heading = null,
  type = "default",
  align = "left",
  menuStyles = null,
  onToggle: propsOnToggle,
  unmountOnClose = false,
  renderOnMount = false,
  tooltipProps = null,
  enablePortal = false,
  appendMenuToBody = false,
  showPortalBackdrop = true,
  closeOnScroll = false,
  parentClassName = "",
  portalBackdropClassName = "",
  ...rest
}: DropdownComponentProps) => {
  const { setIsDropdownOpen, isDropdownOpen } = useContext(DropdownContext);

  const dropdownRef = useRef(null);
  const [appendedMenuWidth, setAppendedMenuWidth] = useState<number | null>(null);

  const dropdownStyles = {
    [FULL_SCREEN]: styles.fullWidth,
    [WIDE_SCREEN]: styles.wideWidth,
    [NARROW_SCREEN]: styles.narrowWidth,
  };

  const onToggle: DropdownComponentProps["onToggle"] = (isOpen, event, metaData) => {
    setIsDropdownOpen(isOpen);
    const addOverFlowHiddenClass = isOpen && type === BOTTOM_DRAWER_DROPDOWN;
    document.body.classList.toggle(styles.overflowHidden, addOverFlowHiddenClass);
    if (isOpen && appendMenuToBody && !enablePortal && type !== BOTTOM_DRAWER_DROPDOWN) {
      const dropdownBounds = dropdownRef.current?.getBoundingClientRect?.();
      setAppendedMenuWidth(dropdownBounds?.width || null);
    }
    propsOnToggle?.(isOpen, event, metaData);
  };

  useEffect(
    () => () => {
      document.body.classList.toggle(styles.overflowHidden, false);
    },
    [],
  );

  useEffect(() => {
    if (!closeOnScroll) return;

    const handleScroll = () => {
      if (isDropdownOpen && dropdownRef.current) {
        const toggle = dropdownRef.current.querySelector('[aria-expanded="true"]');
        if (toggle) {
          (toggle as HTMLElement).click();
        }
      }
    };

    document.addEventListener("scroll", handleScroll, { capture: true, passive: true });

    return () => {
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [closeOnScroll, isDropdownOpen]);

  const renderLabelComponent = () =>
    label &&
    React.cloneElement(label, {
      styles,
      disabled,
      className: classNames(label.props.className, styles.dropdownToggle, {
        [styles.bottomDrawerLabel]: type === BOTTOM_DRAWER_DROPDOWN,
      }),
    });

  const renderLabel = () => {
    if (!label) return null;

    if (tooltipProps && type != BOTTOM_DRAWER_DROPDOWN) {
      return (
        <ToolTip {...tooltipProps} disableOnResponsive>
          {renderLabelComponent()}
        </ToolTip>
      );
    }

    return renderLabelComponent();
  };

  const shouldAppendMenuToBody =
    appendMenuToBody && !enablePortal && type !== BOTTOM_DRAWER_DROPDOWN;

  const menuContent = (
    <Dropdown.Menu
      {...(Dropdown.Menu.defaultProps || {})}
      bsPrefix={styles.dropdownContainer}
      className={classNames(menuClassName, {
        [styles.bottomDrawerDd]: type == BOTTOM_DRAWER_DROPDOWN,
        [dropdownStyles[width]]:
          type != BOTTOM_DRAWER_DROPDOWN && !(shouldAppendMenuToBody && width === "full"),
      })}
      style={
        shouldAppendMenuToBody && width === "full"
          ? {
              ...menuStyles,
              width: appendedMenuWidth ? `${appendedMenuWidth}px` : undefined,
            }
          : menuStyles
      }
      align={align}
      renderOnMount={renderOnMount}
    >
      <BottomIconHideWrapper hideIcon={isDropdownOpen && type === BOTTOM_DRAWER_DROPDOWN}>
        {heading &&
          type == BOTTOM_DRAWER_DROPDOWN &&
          React.cloneElement(heading, {
            styles,
            className: classNames(heading.props.className),
            showCloseIcon: heading.props.showCloseIcon ?? true,
          })}
        {children}
      </BottomIconHideWrapper>
    </Dropdown.Menu>
  );

  return (
    <Dropdown
      {...(Dropdown.defaultProps || {})}
      ref={dropdownRef}
      onToggle={disabled ? null : onToggle}
      bsPrefix={`${styles.dropdown} ${enablePortal ? styles.backdropHide : ""}`}
      {...rest}
    >
      {renderLabel()}
      {(!unmountOnClose || isDropdownOpen) && (
        <DropdownPortal enabled={enablePortal} parentClassName={parentClassName}>
          {enablePortal && (
            <div
              className={classNames(
                isDropdownOpen && showPortalBackdrop && styles.portalDropdownBackdrop,
                isDropdownOpen && showPortalBackdrop && portalBackdropClassName,
              )}
              data-label="bottom-drawer-backdrop"
            />
          )}
          {shouldAppendMenuToBody ? createPortal(menuContent, document.body) : menuContent}
        </DropdownPortal>
      )}
    </Dropdown>
  );
};

export const DropdownComponent = (props: DropdownComponentProps) => (
  <DropdownWrapper>
    <BootstrapDropdown {...props} />
  </DropdownWrapper>
);

/**
 * Utilize the ResponsiveDrawerDropdown to display a dropdown on desktop devices and as a bottom drawer on responsive platforms.
 * Ensure that your package utilizes the CurrentDeviceContext, which should be imported from the ui package.
 */
export const ResponsiveDrawerDropdown = (props: ResponsiveDrawerDropdownProps) => {
  const { isMobileDevice, isTabletDevice } = useContext(CurrentDeviceContext);
  const { showBottomDrawerForTabletDevice = false, ...dropdownProps } = props;

  const showBottomDrawer = isMobileDevice || (isTabletDevice && showBottomDrawerForTabletDevice);
  return (
    <DropdownComponent
      type={showBottomDrawer ? "bottom-drawer" : "default"}
      heading={dropdownProps.heading}
      {...dropdownProps}
    />
  );
};

export const DropdownItem = ({
  children = null,
  styles = defaultStyles,
  className = "",
  isActive = false,
  hasChildBtn = false,
  applyDefaultClass = true,
  ...rest
}: CustomDropdownItemProps) => (
  <Dropdown.Item
    {...(Dropdown.Item.defaultProps || {})}
    bsPrefix="custom-dropdown-item"
    className={`${applyDefaultClass ? styles.dropdownItem : ""} ${className} ${
      isActive ? styles.activeItem : ""
    } ${hasChildBtn ? styles.hasChildBtn : ""}`}
    {...rest}
  >
    {children}
  </Dropdown.Item>
);

export const DropdownHeader = ({ styles = defaultStyles, ...props }: CustomDropdownHeaderProps) => (
  <Dropdown.Header
    {...(Dropdown.Header.defaultProps || {})}
    bsPrefix={styles["dropdown-header"]}
    {...props}
  />
);

export default DropdownComponent;
